/**
 * Shared resolver that maps a thrown mutation/query error to a user-facing
 * i18n key, plus the helpers around it. This is the single source of truth
 * used by:
 *   - the global toast funnel (`providers/mutation-error-listener.tsx`),
 *   - the `/workspace` Errors testbench (so it shows EXACTLY what the funnel
 *     would show), and
 *   - the unit tests.
 *
 * The `errors.*` namespace it resolves against is generated from
 * `@morfi/api-errors` by `scripts/generate-api-error-messages.ts`.
 */

/** The guaranteed-present fallback key. Generated into `en.json` always. */
export const UNKNOWN_KEY = 'errors.unknown';
/** TX-engine "user cancelled" key — the funnel suppresses the toast for it. */
export const CANCELLED_KEY = 'tx.errors.cancelled';

const TX_ERRORS_PREFIX = 'tx.errors.';
const ERRORS_PREFIX = 'errors.';
const API_CODE_KEY_PREFIX = 'errors.codes.';

/** Jail rejection (MOR-2308). Shown with the same copy as the inline notice. */
const TRADING_RESTRICTED_CODE = 'TRADING_RESTRICTED';
const TRADING_RESTRICTED_KEY = 'trade.trading_restricted';

// ALLOW-UNKNOWN: SDK mutation error shape is open — every field is a speculative read off an arbitrary thrown value.
export interface TxErrorLike {
  /** TX-engine i18n key (`tx.errors.*`), set by the on-chain transaction
   *  engine in `packages/backend-sdk-v2/src/tx-engine/...`. */
  // ALLOW-UNKNOWN: speculative field on a thrown mutation error (open shape).
  readonly userMessageKey?: unknown;
  /** REST-error i18n key (`errors.*`), set by `SdkError.userMessage`. */
  // ALLOW-UNKNOWN: speculative field on a thrown mutation error (open shape).
  readonly userMessage?: unknown;
  /** Backend `error.code` on a v2-SDK `SdkError` (REST 4xx/5xx). */
  // ALLOW-UNKNOWN: speculative field on a thrown mutation error (open shape).
  readonly apiCode?: unknown;
  /** Backend error details bag — interpolated into the user-facing message. */
  // ALLOW-UNKNOWN: speculative field on a thrown mutation error (open shape).
  readonly details?: unknown;
}

/** next-intl's `t` is typed against the static message tree; this resolver
 *  intentionally dispatches dynamic SDK-emitted keys, so we narrow the
 *  function shape (not the value) to accept a runtime string. `has` lets us
 *  probe whether a dynamic key exists before committing to it. */
export interface DynamicTranslator {
  (key: string, values?: Record<string, string | number>): string;
  has(key: string): boolean;
}

// ALLOW-UNKNOWN: the thrown error is `unknown` at the mutation boundary.
function readString(error: unknown, field: keyof TxErrorLike): string | undefined {
  if (error === null || typeof error !== 'object') return undefined;
  const v = (error as TxErrorLike)[field];
  return typeof v === 'string' ? v : undefined;
}

/** Read the backend `apiCode` off a thrown error, if present. */
// ALLOW-UNKNOWN: the thrown error is `unknown` at the mutation boundary.
export function pickApiCode(error: unknown): string | undefined {
  return readString(error, 'apiCode');
}

// ALLOW-UNKNOWN: details bag is an open record off the SdkError.
export function pickDetails(error: unknown): Record<string, unknown> {
  if (error === null || typeof error !== 'object') return {};
  const d = (error as TxErrorLike).details;
  if (d === null || typeof d !== 'object') return {};
  return d as Record<string, unknown>;
}

/** Coerce the open-shape `details` bag into next-intl-friendly placeholder
 *  values (string | number). Anything else gets dropped so a stray nested
 *  object doesn't crash the formatter. */
export function detailsToPlaceholders(
  details: Record<string, unknown>,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(details)) {
    if (typeof v === 'string' || typeof v === 'number') out[k] = v;
  }
  return out;
}

// ALLOW-UNKNOWN: the thrown error is `unknown` at the mutation boundary.
function isTradingRestricted(error: unknown): boolean {
  return (
    pickApiCode(error) === TRADING_RESTRICTED_CODE ||
    pickDetails(error).backendErrorCode === TRADING_RESTRICTED_CODE
  );
}

/**
 * Resolve a thrown error to a full dotted i18n key. The ladder runs
 * most-specific → least-specific, and every rung except the last verifies the
 * key exists in the message tree via `t.has` so a raw key string is never
 * shown to the user:
 *
 *   0. A `TRADING_RESTRICTED` rejection — checked first because the relay
 *      wraps it in a generic `TxError` whose `userMessageKey` would otherwise
 *      win with a vague "relay rejected" message.
 *   1. TX-engine `userMessageKey` (`tx.errors.*`) — legacy on-chain path.
 *   2. `errors.codes.<apiCode>` — per-code REST copy from `@morfi/api-errors`.
 *   3. `SdkError.userMessage` (`errors.*`) — the SDK's coarse category key
 *      (e.g. `errors.server.not_found`); the fallback when a REST error's
 *      `apiCode` has no per-code entry (or is absent).
 *   4. `errors.unknown` — guaranteed to exist.
 */
// ALLOW-UNKNOWN: the thrown error is `unknown` at the mutation boundary.
export function pickUserMessageKey(error: unknown, t: DynamicTranslator): string {
  if (isTradingRestricted(error) && t.has(TRADING_RESTRICTED_KEY)) {
    return TRADING_RESTRICTED_KEY;
  }

  const legacy = readString(error, 'userMessageKey');
  if (legacy?.startsWith(TX_ERRORS_PREFIX) === true && t.has(legacy)) {
    return legacy;
  }

  const apiCode = readString(error, 'apiCode');
  if (apiCode !== undefined) {
    const codeKey = `${API_CODE_KEY_PREFIX}${apiCode}`;
    if (t.has(codeKey)) return codeKey;
  }

  const sdkKey = readString(error, 'userMessage');
  if (sdkKey?.startsWith(ERRORS_PREFIX) === true && t.has(sdkKey)) {
    return sdkKey;
  }

  return UNKNOWN_KEY;
}

/**
 * Resolve a thrown error all the way to the display string. Mirrors what the
 * global funnel renders. Returns `null` for the cancelled case (callers
 * suppress the toast). On a next-intl formatter error (placeholder mismatch),
 * falls back to `errors.unknown`.
 */
// ALLOW-UNKNOWN: the thrown error is `unknown` at the mutation boundary.
export function resolveApiErrorMessage(error: unknown, t: DynamicTranslator): string | null {
  const key = pickUserMessageKey(error, t);
  if (key === CANCELLED_KEY) return null;
  const placeholders = detailsToPlaceholders(pickDetails(error));
  try {
    return t(key, placeholders);
  } catch {
    // SILENT-CATCH: next-intl placeholder mismatch — degrade to the safe
    // `errors.unknown` copy rather than surface a formatter exception.
    try {
      return t(UNKNOWN_KEY);
    } catch {
      // SILENT-CATCH: even the fallback key failed to format; return a raw
      // key string so the toast never throws.
      const apiCode = pickApiCode(error);
      return apiCode !== undefined ? `${key} (${apiCode})` : key;
    }
  }
}
